import React, { useMemo } from 'react';
import { FormLabel, Box } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { isAfter, isBefore, isValid } from 'date-fns';
import { minimalInputTheme } from '@/app/theme';
import { useUpdateDealRowMutation } from '@/features/deals/dealsApi';
import { useUpdateInsightClaimMutation } from '@/features/claims/ticketsApi';
import { useObjectContext } from '@/hooks/useObjectContext';
import { Timestamp } from 'firebase/firestore';
import { safelyParseDate } from '@/utils/date';

const minDate = new Date();

// dealKey is one of: 'renewalDate' | 'estimatedCloseDate' (Firestore, on the deal
// row) | 'LossDate' | 'ReportedDate' (Insight, on the claim).
export const DealCardDateSelect = ({
	title,
	dealKey,
	disableFuture,
	disablePast,
	minimal = true,
}) => {
	const { dealRow, claim } = useObjectContext();

	const selectedDate = useMemo(() => {
		if (dealKey === 'renewalDate' || dealKey === 'estimatedCloseDate') {
			return safelyParseDate(dealRow?.[dealKey]);
		}
		if (dealKey === 'LossDate') {
			return safelyParseDate(claim?.LossDate);
		}
		if (dealKey === 'ReportedDate') {
			return safelyParseDate(claim?.[dealKey]) ?? safelyParseDate(dealRow?.[dealKey]);
		}
		return null;
	}, [dealKey, claim, dealRow]);

	const [updateDealRow, { isLoading: isUpdatingDealRow }] = useUpdateDealRowMutation();
	const [updateInsightClaim, { isLoading: isUpdatingInsight }] = useUpdateInsightClaimMutation();

	const handleDateChange = async (value) => {
		const date = value;
		// Get the local time zone offset in minutes
		const timezoneOffset = date.getTimezoneOffset();
		// Adjust the date to UTC by adding the time zone offset
		date.setMinutes(date.getMinutes() - timezoneOffset);
		// Set the time to midnight UTC
		const timestamp = date.setUTCHours(0, 0, 0, 0);

		try {
			if (dealKey === 'estimatedCloseDate') {
				return await updateDealRow({
					dealId: dealRow?.dealId,
					docId: dealRow?.id,
					properties: {
						estimatedCloseDate: Timestamp.fromDate(date),
					},
				}).unwrap();
			}

			if (dealKey === 'renewalDate') {
				return await updateDealRow({
					dealId: dealRow?.dealId,
					docId: dealRow?.id,
					properties: {
						renewalDate: timestamp,
					},
				}).unwrap();
			}

			if (dealKey === 'LossDate' || dealKey === 'ReportedDate') {
				const formattedDate = safelyParseDate(timestamp).toISOString().slice(0, 10);
				await updateInsightClaim({
					dealId: dealRow?.id,
					properties: {
						[dealKey]: formattedDate,
					},
				}).unwrap();
			}
		} catch (error) {
			console.error('Error updating deal properties:', error);
		}
	};

	return (
		<Box sx={{ width: '100%' }}>
			<FormLabel>{title}</FormLabel>
			<LocalizationProvider dateAdapter={AdapterDateFns} style={{ width: '100%' }}>
				<DatePicker
					name={dealKey}
					className={minimal === true ? 'minimal-input' : null}
					disablePast={disablePast}
					disableFuture={disableFuture}
					disabled={isUpdatingDealRow || isUpdatingInsight}
					format='dd/MM/yyyy'
					value={selectedDate}
					slotProps={{
						textField: {
							size: minimal ? 'small' : 'medium',
							...(minimal && { sx: minimalInputTheme }),
							variant: minimal ? 'standard' : 'outlined',
							fullWidth: true,
						},
					}}
					sx={{
						...(minimal ? minimalInputTheme : {}),
						width: '100%',
					}}
					onChange={(value) => {
						let newDate = value;
						if (disablePast) {
							newDate = !isValid(value) || isAfter(minDate, value) ? minDate : value;
						} else if (disableFuture) {
							newDate = !isValid(value) || isBefore(minDate, value) ? minDate : value;
						}
						handleDateChange(newDate);
					}}
				/>
			</LocalizationProvider>
		</Box>
	);
};
